import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Building2, Home, ShoppingBag, Key, User, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UserTypeSelection() {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [propertyInterest, setPropertyInterest] = useState('');
  const [userType, setUserType] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!propertyInterest || !userType) {
      toast.error('Please select all options');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/update-profile`,
        {
          property_interest: propertyInterest,
          user_type: userType
        },
        { withCredentials: true }
      );

      toast.success('Profile updated successfully!');
      const from = location.state?.from || '/dashboard';
      navigate(from, { replace: true });
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="user-type-selection-page">
      <div className="max-w-4xl w-full">
        {/* Logo and Header */}
        <div className="text-center mb-12">
          <img
            src="https://customer-assets.emergentagent.com/job_kochi-realty/artifacts/ojw054q1_TB%20Logo.png"
            alt="True Bridge"
            className="h-24 mx-auto mb-6"
          />
          <h1 className="text-4xl md:text-5xl font-heading font-bold mb-3" data-testid="selection-heading">
            Welcome to True Bridge
          </h1>
          <p className="text-lg text-slate-600 mb-2">Collaboration. Innovation. Trust.</p>
          <p className="text-slate-600">Let's personalize your experience</p>
        </div>

        {/* Step 1: Property Interest */}
        {step === 1 && (
          <div className="bg-white p-8 rounded-lg shadow-sm border border-slate-100 fade-in" data-testid="property-interest-step">
            <h2 className="text-2xl font-heading font-semibold mb-6 text-center">
              What type of properties are you interested in?
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
              <button
                data-testid="select-residential"
                onClick={() => {
                  setPropertyInterest('residential');
                  setStep(2);
                }}
                className="group p-8 border-2 border-slate-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-all duration-300 text-center"
              >
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors">
                  <Home className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Residential</h3>
                <p className="text-sm text-slate-600">Homes, apartments, villas</p>
              </button>

              <button
                data-testid="select-commercial"
                onClick={() => {
                  setPropertyInterest('commercial');
                  setStep(2);
                }}
                className="group p-8 border-2 border-slate-200 rounded-lg hover:border-primary hover:bg-primary/5 transition-all duration-300 text-center"
              >
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 transition-colors">
                  <Building2 className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Commercial</h3>
                <p className="text-sm text-slate-600">Offices, shops, warehouses</p>
              </button>
            </div>
          </div>
        )}

        {/* Step 2: User Type */}
        {step === 2 && (
          <div className="bg-white p-8 rounded-lg shadow-sm border border-slate-100 fade-in" data-testid="user-type-step">
            <button
              onClick={() => setStep(1)}
              className="text-slate-600 hover:text-primary mb-6 flex items-center gap-2"
            >
              ← Back
            </button>
            
            <h2 className="text-2xl font-heading font-semibold mb-2 text-center">
              How would you like to use True Bridge?
            </h2>
            <p className="text-slate-600 text-center mb-8">
              Selected: <span className="font-medium text-primary capitalize">{propertyInterest}</span> Properties
            </p>
            
            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {/* For Sale/Rent Properties */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-center mb-4">Looking to Buy or Sell</h3>
                
                <button
                  data-testid="select-buyer"
                  onClick={() => setUserType('buyer')}
                  className={`w-full group p-6 border-2 rounded-lg transition-all duration-300 text-center ${
                    userType === 'buyer'
                      ? 'border-primary bg-primary/5'
                      : 'border-slate-200 hover:border-primary hover:bg-primary/5'
                  }`}
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <ShoppingBag className="w-6 h-6 text-primary" />
                  </div>
                  <h4 className="text-lg font-semibold mb-1">Buyer</h4>
                  <p className="text-sm text-slate-600">I want to buy a property</p>
                </button>

                <button
                  data-testid="select-seller"
                  onClick={() => setUserType('seller')}
                  className={`w-full group p-6 border-2 rounded-lg transition-all duration-300 text-center ${
                    userType === 'seller'
                      ? 'border-primary bg-primary/5'
                      : 'border-slate-200 hover:border-primary hover:bg-primary/5'
                  }`}
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <User className="w-6 h-6 text-primary" />
                  </div>
                  <h4 className="text-lg font-semibold mb-1">Seller</h4>
                  <p className="text-sm text-slate-600">I want to sell my property</p>
                </button>
              </div>

              {/* For Rental Properties */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-center mb-4">Looking to Rent</h3>
                
                <button
                  data-testid="select-tenant"
                  onClick={() => setUserType('tenant')}
                  className={`w-full group p-6 border-2 rounded-lg transition-all duration-300 text-center ${
                    userType === 'tenant'
                      ? 'border-primary bg-primary/5'
                      : 'border-slate-200 hover:border-primary hover:bg-primary/5'
                  }`}
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Key className="w-6 h-6 text-primary" />
                  </div>
                  <h4 className="text-lg font-semibold mb-1">Tenant</h4>
                  <p className="text-sm text-slate-600">I'm looking for a rental</p>
                </button>

                <button
                  data-testid="select-landlord"
                  onClick={() => setUserType('landlord')}
                  className={`w-full group p-6 border-2 rounded-lg transition-all duration-300 text-center ${
                    userType === 'landlord'
                      ? 'border-primary bg-primary/5'
                      : 'border-slate-200 hover:border-primary hover:bg-primary/5'
                  }`}
                >
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Briefcase className="w-6 h-6 text-primary" />
                  </div>
                  <h4 className="text-lg font-semibold mb-1">Landlord</h4>
                  <p className="text-sm text-slate-600">I have properties to rent</p>
                </button>
              </div>
            </div>

            <div className="mt-8 text-center">
              <Button
                data-testid="submit-user-type"
                onClick={handleSubmit}
                disabled={!userType || loading}
                className="px-12 h-12 bg-primary hover:bg-primary/90 text-white font-medium"
              >
                {loading ? 'Saving...' : 'Continue'}
              </Button>
            </div>
          </div>
        )}

        <p className="text-center text-sm text-slate-500 mt-6">
          You can change these preferences later in your account settings
        </p>
      </div>
    </div>
  );
}